// Camada de chamada HTTP do front (better-fetch). Fronteira única com o back: todo request passa aqui.
// baseURL vem do .env (VITE_API_URL) — aponta direto ao back (CORS configurado no back e no front).
//
// Este módulo é a FUNDAÇÃO: a instância `api`, a base do back, a credencial
// temporária e a normalização de erro. Os módulos por domínio (./brands.js, …)
// importam daqui; a dependência nunca vai no sentido contrário — se este
// arquivo precisasse conhecer um domínio, o domínio estaria no lugar errado.
import { createFetch } from "@better-fetch/fetch";
import i18n from "../i18n.js";

// Credencial curta da tela pública de click-to-call. Ela existe apenas nesta
// instância JavaScript: nunca vai para localStorage/sessionStorage nem para URL.
// Isso contorna o bloqueio de cookie third-party dentro do iframe do HubSpot.
let temporaryAccessToken: string | null = null;
let temporaryAccessActive = false;

export function clearTemporaryAccessToken(): void {
  temporaryAccessToken = null;
  temporaryAccessActive = false;
}

export function activateTemporaryAccess(): void {
  temporaryAccessActive = true;
}

export function deactivateTemporaryAccess(): void {
  temporaryAccessActive = false;
}

export function hasTemporaryAccessToken(): boolean {
  return temporaryAccessActive && temporaryAccessToken !== null;
}

// A credencial acima é ESTADO DE MÓDULO: existe uma vez só, nesta instância do
// JavaScript. Quem escreve nela (POST /v1/access/exchange) e quem a lê fora do
// `onRequest` (o terminate com `keepalive`, que não passa pelo better-fetch)
// moram em outros módulos — por isso o par de acessores abaixo, em vez de
// exportar a variável: uma cópia do valor em outro módulo seria um segundo
// token, que ninguém limparia no clearTemporaryAccessToken.
export function setTemporaryAccessToken(token: string): void {
  temporaryAccessToken = token;
}

// Token só quando o acesso temporário está ATIVO — mesma condição do
// `onRequest`. Null é a resposta honesta para "não há Bearer a enviar".
export function activeTemporaryAccessToken(): string | null {
  return temporaryAccessActive ? temporaryAccessToken : null;
}

// Base do back (VITE_API_URL), sem barra final — evita URLs com `//` (ex.: `${base}//api/auth`).
export const API_BASE_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:3000").replace(
  /\/+$/,
  "",
);

// Instância configurada e reutilizável. Default do better-fetch: retorna { data, error } (não lança) —
// o erro é um valor explícito, no espírito anti-placebo: quem chama decide, nunca inventa dado.
export const api = createFetch({
  baseURL: API_BASE_URL,
  retry: 0, // sem retry silencioso — falha é honesta
  headers: { Accept: "application/json" },
  credentials: "include", // envia o cookie de sessão do Better Auth (CORS com credentials no back)
  onRequest(context) {
    if (temporaryAccessActive && temporaryAccessToken) {
      context.headers.set("Authorization", `Bearer ${temporaryAccessToken}`);
    }
    return context;
  },
});

// Códigos de erro de domínio do back (envelope { error: <código> }) → mensagem PT
// legível e ACIONÁVEL (diz o que fazer, não só o sintoma). Sem este mapa o usuário
// via "409 erro desconhecido": o código ficava no corpo, que o toApiError nem lia —
// e em HTTP/2 o statusText vem vazio, então não havia sequer "Conflict" para mostrar.
// Exportado: é a mesma fonte que importar-leads-dialog.tsx usa para traduzir os
// `reason` do preview linha a linha (os endpoints de lead usam estes mesmos
// códigos) — uma lista só, em vez de duas listas dos mesmos códigos divergindo.
export const DOMAIN_ERROR_MESSAGES: Record<string, string> = {
  // Ligação com IA (POST /v1/calls/ai): pré-registro da sessão no voice-talk.
  ai_script_unavailable:
    "Nenhum script de IA ativo para esta organização. Ative um em Gestão › Script da IA.",
  ai_session_unavailable: "A IA de voz está indisponível agora. Tente de novo em instantes.",
  ari_unavailable: "Telefonia indisponível no momento. Tente de novo em instantes.",
  org_no_did: "Nenhum número de origem (DID) configurado para esta organização.",
  sip_not_provisioned: "Seu ramal ainda não está provisionado. Fale com o gestor.",
  agent_busy: "Você já tem uma ligação em andamento. Encerre-a antes de iniciar outra.",
  agent_unavailable: "Você está indisponível no discador. Retome a sessão para receber leads.",
  invalid_target: "Número de destino inválido.",
  // Discagem manual (POST /v1/calls sem leadId/externalLeadId): toggle da marca
  // desligado, ou motivo obrigatório ausente.
  manual_dial_disabled:
    "A discagem manual está desligada para esta organização. Fale com o gestor.",
  reason_required: "Informe o motivo da ligação.",
  // Identidade da marca (RCD): o back confere o dígito verificador do CNPJ, não
  // só o tamanho — daí a mensagem falar em "válido" e não em "14 dígitos".
  invalid_cnpj: "CNPJ inválido. Confira os números digitados.",
  not_ai_call: "Esta ligação não foi conduzida pela IA.",
  no_organization: "Sua sessão não tem uma organização ativa.",
  forbidden: "Você não tem permissão para esta ação.",
  // Construtor de roteiros (integração com o LAI Compass).
  compass_not_configured:
    "Integração com o LAI Compass não configurada no servidor (COMPASS_URL/COMPASS_API_KEY).",
  org_not_linked:
    "Esta organização não está vinculada a uma marca no LAI Compass. Vincule a marca para gerenciar roteiros.",
  // Cadastro manual e importação de leads (POST /v1/leads, /v1/leads/import).
  lead_duplicado: "Já existe um lead com este telefone nesta marca.",
  telefone_invalido: "Telefone inválido. Confira o DDD e o número.",
  status_invalido: "Etapa informada não existe nesta marca.",
  responsavel_invalido: "Responsável informado não faz parte desta marca.",
  erro_interno: "Erro interno ao processar esta linha. Tente novamente.",
};

export function domainErrorMessage(code: string): string | undefined {
  const fallback = DOMAIN_ERROR_MESSAGES[code];
  return fallback
    ? i18n.t(`apiErrors.domain.${code}`, {
        defaultValue: fallback,
      })
    : undefined;
}

// Normaliza um erro do better-fetch numa Error legível (com status quando houver).
// Útil para usar dentro de queryFn do TanStack Query, que espera throw no erro.
// O better-fetch faz spread do corpo JSON no objeto de erro, então o código de
// domínio chega em `error.error` (envelope do back) — daí a preferência por ele.
// Erro estendido com o `code` de domínio + `status`, para quem precisa RAMIFICAR
// pelo código (não só exibir a mensagem) — ex.: cair no fluxo legado quando o
// back responde compass_not_configured. `undefined` quando não há código.
export interface ApiError extends Error {
  code?: string;
  status?: number;
  // Presente no 409 de POST /v1/leads (telefone duplicado na marca): id do lead
  // já existente, para a tela oferecer abrir ele em vez de só reclamar do erro.
  leadId?: string;
}

export function toApiError(
  path: string,
  error: {
    status?: number;
    statusText?: string;
    message?: string;
    error?: string;
    code?: string;
    leadId?: string;
  },
): ApiError {
  const status = error.status ? `${error.status} ` : "";
  const domainCode = error.error || error.code;
  const detail =
    (domainCode && domainErrorMessage(domainCode)) ||
    domainCode ||
    error.statusText ||
    error.message ||
    i18n.t("apiErrors.unknown");
  const err: ApiError = new Error(
    i18n.t("apiErrors.requestFailed", { path, status, detail }).trim(),
  );
  err.code = domainCode;
  err.status = error.status;
  err.leadId = error.leadId;
  return err;
}

// Upload multipart (voz .wav / script .pdf / áudio de URA): o wrapper `api()` é
// focado em JSON, então usamos fetch cru com credentials para preservar o
// boundary do FormData.
//
// Mora aqui — e não no domínio que subia arquivo — porque é a MESMA fronteira
// HTTP que `api`: base do back, credencial da sessão e erro traduzido. Três
// domínios diferentes sobem arquivo (URA, roteiro da IA, script de vendas); com
// o helper num deles, os outros dois o importariam de um vizinho sem parentesco.
// Não é reexportado pelo barrel: era privado de api.ts e continua interno a
// src/api/.
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    body: form,
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const b = (await res.json()) as { error?: string; detail?: string };
      detail = b.error ?? b.detail ?? detail;
    } catch {
      /* corpo não-JSON: mantém statusText */
    }
    throw new Error(i18n.t("apiErrors.postFailed", { path, status: res.status, detail }).trim());
  }
  return (await res.json()) as T;
}
